/**
 * Analytics Portal V1 — managed-entity selector (Phase 9, plan §12.1).
 *
 * The list comes from `GET /api/analytics/entities`, which already contains **only** entities
 * the viewer may open analytics for. Nothing here filters, guesses or constructs an entity, so
 * a selection can never widen access; the server re-authorizes every dashboard request anyway.
 */
import React from "react";
import { Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const EntitySelector = ({ entities = [], value, onChange, loading, disabled }) => {
  const [open, setOpen] = React.useState(false);
  const active = entities.find((row) => row.entity_id === value);

  if (!loading && entities.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="secondary" size="sm" disabled={disabled || loading}
                className="max-w-[16rem] justify-between gap-2"
                aria-label="Select the entity to analyse"
                data-testid="analytics-entity-trigger">
          <span className="truncate">
            {loading ? "Loading…" : (active?.label || active?.name || "Select an entity")}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="max-h-[320px] w-[18rem] overflow-y-auto p-1.5">
        <div role="listbox" aria-label="Managed entities">
          {entities.map((row) => (
            <button
              key={row.entity_id} type="button" role="option"
              aria-selected={row.entity_id === value}
              onClick={() => { onChange(row.entity_id); setOpen(false); }}
              className="flex w-full items-start justify-between gap-2 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-accent"
              data-testid={`analytics-entity-option-${row.entity_id}`}
            >
              <span className="min-w-0">
                <span className="block truncate text-sm text-foreground">
                  {row.label || row.name || "Untitled"}
                </span>
                <span className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                  <span className="capitalize">{row.role?.replace(/_/g, " ")}</span>
                  {row.organizer_inherited && <span>· via organizer</span>}
                  {row.archived && (
                    <span className="rounded-full border border-border px-1.5 uppercase tracking-wide">
                      Archived
                    </span>
                  )}
                </span>
              </span>
              {row.entity_id === value && (
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
              )}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
